import React, { useState } from 'react';
import {Formik, Form, ErrorMessage, FormikHelpers} from 'formik';
import * as Yup from 'yup';
import {Form as BootstrapForm, Button, Alert, FormGroup, FormLabel, FormSelect, Row, Col} from 'react-bootstrap';
import {X} from "react-bootstrap-icons";

interface FormValues {
    file: File | null;
    roleId: number | undefined;
}

function UserFileUploadForm(props: {
    tenantId: number
    roles: any[]
    onInvite: (roleId: number, emails: string[]) => Promise<void>
    onSuccess?: () => void
}) {
    const [emails, setEmails] = useState<string[]>([]);

    const initialValues: FormValues = {
        file: null,
        roleId: undefined,
    };

    const validationSchema = Yup.object().shape({
        file: Yup.mixed()
            .required("A file is required")
            .test("fileSize", "File is too large", value => {
                return value && (value as File).size <= 10048576; // 10MB limit
            })
            .test("type", "Unsupported file format", (value) => {
                return value && (value as File).name.endsWith('.csv');
            }),
        roleId: Yup.number().required("Please select a role"),
    });

    const parseCSV = (file: File): Promise<string[]> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const text = e.target?.result as string;
                const lines = text.split('\n').map(line => line.trim()).filter(line => line);
                const emailList = lines.map(line => {
                    // Handle both simple email lists and CSV with email column
                    const columns = line.split(',');
                    const email = columns[0].trim().replace(/"/g, ''); // Remove quotes if present
                    return email;
                }).filter(email => {
                    // Basic email validation
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    return emailRegex.test(email);
                });
                resolve(emailList);
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsText(file);
        });
    };

    const onSubmit = async (
        values: FormValues,
        {setSubmitting}: FormikHelpers<FormValues>
    ) => {
        setSubmitting(true);
        try {
            const emailList = await parseCSV(values.file!!);
            setEmails(emailList);

            await props.onInvite(values.roleId!!, emailList);

            if (props.onSuccess) {
                props.onSuccess();
            }
        } catch (error) {
            console.error('Error processing file:', error);
        } finally {
            setSubmitting(false);
        }
    };

    const removeEmail = (email: string) => {
        setEmails(emails.filter(item => item !== email));
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
        >
            {({setFieldValue, values, isSubmitting}) => (
                <Form>
                    <FormGroup className="mb-3">
                        <FormLabel>Select Role</FormLabel>
                        <FormSelect
                            name="roleId"
                            value={values.roleId || ''}
                            onChange={(e) => {
                                const value = e.target.value;
                                setFieldValue('roleId', value ? Number.parseInt(value) : undefined);
                            }}
                        >
                            <option value="" label="Select role" />
                            {props.roles.map(role => (
                                <option key={role.id} value={role.id}>
                                    {role.name}
                                </option>
                            ))}
                        </FormSelect>
                        <ErrorMessage name="roleId">
                            {(msg) => <Alert variant="danger">{msg}</Alert>}
                        </ErrorMessage>
                    </FormGroup>

                    <BootstrapForm.Group className="mb-3">
                        <BootstrapForm.Label>Upload CSV File (with email addresses)</BootstrapForm.Label>
                        <BootstrapForm.Control
                            type="file"
                            name="file"
                            accept=".csv"
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                const file = event.currentTarget.files?.[0] || null;
                                setFieldValue("file", file);
                                if (!file) {
                                    setEmails([]);
                                }
                            }}
                        />
                        <ErrorMessage name="file">
                            {(msg) => <Alert variant="danger">{msg}</Alert>}
                        </ErrorMessage>
                    </BootstrapForm.Group>

                    {emails.length > 0 && (
                        <div className="mb-3">
                            <h6>Emails from file ({emails.length}):</h6>
                            {emails.map((email, index) => (
                                <Row key={index} className="mb-1">
                                    <Col>{email}</Col>
                                    <Col xs="auto">
                                        <a href="#" onClick={(e) => { e.preventDefault(); removeEmail(email); }}>
                                            <X />
                                        </a>
                                    </Col>
                                </Row>
                            ))}
                        </div>
                    )}

                    <div className="mt-4 d-grid gap-2">
                        <Button variant="primary" type="submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Processing...' : 'Send Invites'}
                        </Button>
                    </div>
                </Form>
            )}
        </Formik>
    );
}

export default UserFileUploadForm;